import { Box, Button, CircularProgress, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import axios from "axios";
import { MaterialReactTable } from "material-react-table";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import dayjs, { Dayjs } from "dayjs";
import Iconify from "../../../minimals/components/iconify/Iconify";
import { columns } from "./Columns";
import { LedgerType, LedgerNote } from "./types";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { fCurrency } from "../../utils/formatNumber";
import TransactionHistoryModal from "./TransactionHistoryModal";
import LedgerTransferModal from "./LedgerTransferModal";
import PendingCheckModal from "./PendingCheckModal";
import MiscellaneousDebitModal from "./MiscellaneousDebitModal";
import PendingWireModal from "./NewPendingWireModal";
import order from "../../taskManagement/orderInfo/Order";
import NoteBadge from "../../checks/checkNotes/NoteBadge";
import NotesModal from "./NotesModal";
import OpenInHomePageButton from "../../searches/OpenInHomePageButton";
import RelatedLedgers from "./RelatedLedgers";

export default function TransactionLedger() {
  const [loading, setLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [transactionLedgers, setTransactionLedgers] = useState<LedgerType[]>([]);
  const [ledgerBalance, setLedgerBalance] = useState("");
  const [trustAccount, setTrustAccount] = useState<{ code: string; bankName: string } | undefined>(undefined);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [openNewOptions, setOpenNewOptions] = useState(false);
  const [openLedgerTransferModal, setOpenLedgerTransferModal] = useState(false);
  const [openPendingCheckModal, setOpenPendingCheckModal] = useState(false);
  const [openMiscellaneousDebitModal, setOpenMiscellaneousDebitModal] = useState(false);
  const [openPendingWireModal, setOpenPendingWireModal] = useState(false);
  const [openNotesModal, setOpenNotesModal] = useState(false);
  const [separateIncomingOutgoing, setSeparateIncomingOutgoing] = useState(true);
  const initialFilterValue = ["Pending", "Posted", "Voided"];
  const [notes, setNotes] = useState<LedgerNote[]>([]);
  const { orderNumber } = useParams();
  const incomingTransactionLedgers = transactionLedgers.filter((transaction) => !transaction.isDebit);
  const outgoingTransactionLedgers = transactionLedgers.filter((transaction) => transaction.isDebit);

  const getTransactions = async () => {
    setLoading(true);
    const url = `/proxy/api/ledgers/gettransactions?orderNumber=${orderNumber}`;
    const { data } = await axios.get(url);
    setTransactionLedgers(data);
    setLoading(false);
  };
  const getLedgerBalance = async () => {
    setBalanceLoading(true);
    const { data } = await axios.get(`/proxy/api/ledgers/GetOrderAndLedgerBalance?ordernumber=${orderNumber}`);
    setLedgerBalance(data.ledgerBalance);
    setTrustAccount(data.trustAccount);
    setBalanceLoading(false);
  };

  const getNotes = async () => {
    const { data } = await axios.get<LedgerNote[]>(`/proxy/api/ledgers/GetLedgerNotes?orderNumber=${orderNumber}`);
    setNotes(data);
  };

  const handleHistoryModal = (id: string) => {
    setOpenHistoryModal(true);
    setTransactionId(id);
  };

  const renderLedgerTable = (data: LedgerType[], hideInOutColumn: boolean) => (
    <MaterialReactTable
      enableStickyHeader
      columns={columns(handleHistoryModal, orderNumber!, () => getTransactions(), () => getLedgerBalance())}
      data={data}
      initialState={{
        density: "compact",
        pagination: { pageIndex: 0, pageSize: 30 },
        columnFilters: [{ id: "status", value: initialFilterValue }],
        columnVisibility: { isDebit: !hideInOutColumn }
      }}
      muiTableBodyProps={{
        sx: {
          "& .MuiTableRow-root:hover .MuiTableCell-root": {
            backgroundColor: "inherit !important"
          }
        }
      }}
      state={{ showSkeletons: loading }}
    />
  );

  useEffect(() => {
    window.addEventListener("click", () => {
      setOpenNewOptions(false);
    });
    return () => {
      window.removeEventListener("click", () => {
        setOpenNewOptions(false);
      });
    };
  }, []);
  useEffect(() => {
    getTransactions();
    getLedgerBalance();
    getNotes();
  }, [orderNumber]);

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" marginBottom={4} position="relative">
        <Typography
          variant="h4"
          sx={{
            position: "absolute",
            left: "50%",
            top: "10%",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none"
          }}
        >
          Transaction Ledger
        </Typography>

        <Stack direction="column" spacing={2}>
          <Typography variant="h4">{orderNumber}<OpenInHomePageButton orderNumber={orderNumber}/></Typography>
          <Typography display={"flex"}>
            Trust Account{" "}
            <Typography sx={{ color: "grey" }} variant="subtitle1" mx={1}>
              &bull;
            </Typography>{" "}
            {balanceLoading ? (
              <CircularProgress size={16} />
            ) : trustAccount ? (
              `${trustAccount?.bankName} [${trustAccount?.code}]`
            ) : (
              "N/A"
            )}
          </Typography>
          <Typography display={"flex"}>
            Ledger Balance{" "}
            <Typography sx={{ color: "grey" }} variant="subtitle1" mx={1}>
              &bull;
            </Typography>{" "}
            {balanceLoading ? <CircularProgress size={16} /> : fCurrency(ledgerBalance)}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={separateIncomingOutgoing}
                onChange={(event) => setSeparateIncomingOutgoing(event.target.checked)}
              />
            }
            label="Separate incoming / outgoing"
          />
          <NoteBadge onClick={async () => setOpenNotesModal(true)} length={notes.length} color="secondary" />
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={(e) => {
              e.stopPropagation();
              setOpenNewOptions(!openNewOptions);
            }}
          >
            New
          </Button>
        </Stack>
        {openNewOptions && (
          <Stack
            sx={{
              borderRadius: 1,
              padding: 2,
              position: "absolute",
              top: 68,
              right: 0,
              backgroundColor: "#fff",
              border: "1px solid #919eab",
              zIndex: 500
            }}
          >
            <Typography
              sx={{
                cursor: "pointer",
                padding: 1,
                borderRadius: 1,
                ":hover": { backgroundColor: "#f5f5f5" }
              }}
              onClick={() => setOpenLedgerTransferModal(true)}
            >
              Ledger Transfer
            </Typography>
            <Typography
              sx={{
                cursor: "pointer",
                padding: 1,
                borderRadius: 1,
                ":hover": { backgroundColor: "#f5f5f5" }
              }}
              onClick={() => setOpenPendingCheckModal(true)}
            >
              Pending Check
            </Typography>
            <Typography
              sx={{ cursor: "pointer", padding: 1, borderRadius: 1, ":hover": { backgroundColor: "#f5f5f5" } }}
              onClick={() => setOpenMiscellaneousDebitModal(true)}
            >
              Miscellaneous Debit
            </Typography>
            <Typography
              sx={{
                cursor: "pointer",
                padding: 1,
                borderRadius: 1,
                ":hover": { backgroundColor: "#f5f5f5" }
              }}
              onClick={() => setOpenPendingWireModal(true)}
            >
              Pending Wire
            </Typography>
          </Stack>
        )}
      </Stack>
      {orderNumber && <RelatedLedgers ledgerName={orderNumber} refreshKey={`${ledgerBalance}:${transactionLedgers.length}`} />}
      {separateIncomingOutgoing ? (
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant="h6">Incoming Transactions</Typography>
            {renderLedgerTable(incomingTransactionLedgers, true)}
          </Stack>
          <Stack spacing={1}>
            <Typography variant="h6">Outgoing Transactions</Typography>
            {renderLedgerTable(outgoingTransactionLedgers, true)}
          </Stack>
        </Stack>
      ) : (
        renderLedgerTable(transactionLedgers, false)
      )}
      {openHistoryModal && (
        <TransactionHistoryModal
          open={openHistoryModal}
          closeModal={() => setOpenHistoryModal(false)}
          ledgerName={orderNumber!}
          onChangeActionCompleted={() => {
            setOpenHistoryModal(false);
            getTransactions();
          }}
          transaction={transactionLedgers.find((t) => t.id == transactionId)!}
        />
      )}
      {openLedgerTransferModal && (
        <LedgerTransferModal
          open={openLedgerTransferModal}
          closeModal={() => setOpenLedgerTransferModal(false)}
          orderNumber={orderNumber}
          ledgerBalance={+ledgerBalance}
          handleRefreshList={() => getTransactions()}
          handleRefreshBalance={() => getLedgerBalance()}
        />
      )}
      {openPendingCheckModal && (
        <PendingCheckModal
          open={openPendingCheckModal}
          closeModal={() => setOpenPendingCheckModal(false)}
          orderNumber={orderNumber}
          handleRefreshList={() => getTransactions()}
          handleRefreshBalance={() => getLedgerBalance()}
          ledgerBalance={+ledgerBalance}
        />
      )}

      {openMiscellaneousDebitModal && (
        <MiscellaneousDebitModal
          open={openMiscellaneousDebitModal}
          closeModal={() => setOpenMiscellaneousDebitModal(false)}
          orderNumber={orderNumber!}
          ledgerBalance={balanceLoading || ledgerBalance === "" ? null : +ledgerBalance}
          handleRefreshList={getTransactions}
          handleRefreshBalance={getLedgerBalance}
        />
      )}

      {openPendingWireModal && (
        <PendingWireModal
          open={openPendingWireModal}
          closeModal={() => setOpenPendingWireModal(false)}
          orderNumber={orderNumber}
          handleRefreshList={() => getTransactions()}
          handleRefreshBalance={() => getLedgerBalance()}
        />
      )}
      {openNotesModal && (
        <NotesModal
          open={openNotesModal}
          onClose={() => setOpenNotesModal(false)}
          title={`Ledger ${orderNumber}`}
          notes={notes}
          refetchData={getNotes}
          orderNumberDisplay={orderNumber}
        />
      )}
    </>
  );
}
